import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import type { Game } from '@/game/Game';
import { RECIPES, type RecipeId } from '@/game/minigames/cooking/recipes';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave } from '@/ui/services/ServicesContext';
import styles from './MiniGameOverlay.module.css';

const SAY_MS = 2200;

/**
 * Bếp của bà over the canvas (minigames.md §4.3, §4.7): the recipe card (lines struck through as they are
 * done), the step's one-line instruction, Sếp Mèo's one-liners, and the thumb-zone buttons: "Giữ để rót / đun"
 * on a hold step, "Xong món" on the plating step.
 */
export function CookingHud({ game, recipe, asking }: { game: Game; recipe: RecipeId; asking: boolean }) {
  const { t } = useI18n();
  const cooking = useStore(game.miniGameUi, (s) => s.run?.cooking);
  const playing = useStore(game.miniGameUi, (s) => s.run?.phase === 'playing');
  const tapToHold = useSave((s) => s.settings.tapToHold);
  const [cardOpen, setCardOpen] = useState(false);
  // The one-liner shows for SAY_MS, then this id is marked as gone.
  const [goneSayId, setGoneSayId] = useState(0);
  const sayId = cooking?.say?.id ?? 0;
  useEffect(() => {
    if (sayId === 0) return;
    const timer = setTimeout(() => {
      setGoneSayId(sayId);
    }, SAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [sayId]);
  const say = sayId !== goneSayId ? (cooking?.say?.key ?? null) : null;

  if (!cooking || !playing) return null;
  const { step, held } = cooking;
  const level = RECIPES[recipe].level;
  const lines = [1, 2, 3, 4] as const;
  const holdLabel = tapToHold
    ? t(held ? 'ui.cooking.tap_stop' : 'ui.cooking.tap_start')
    : t(step.holdKind === 'boil' ? 'ui.cooking.hold_boil' : 'ui.cooking.hold_pour');

  return (
    <>
      <button
        type="button"
        className={styles.recipeCard}
        aria-expanded={cardOpen}
        aria-label={t('ui.cooking.recipe_card')}
        data-open={cardOpen}
        onClick={() => {
          setCardOpen((v) => !v);
        }}
      >
        <span className={styles.recipeStep}>
          {t('ui.cooking.step_of', { n: step.index + 1, total: step.total })}
        </span>
        <ol className={styles.recipeLines}>
          {lines.map((n) => (
            <li
              key={n}
              value={n}
              data-state={n < step.line ? 'done' : n === step.line ? 'now' : 'todo'}
              hidden={!cardOpen && (n < step.line || n > step.line + 1)}
            >
              {t(`minigame.mg_cooking.l${String(level)}_line_${String(n)}`)}
            </li>
          ))}
        </ol>
      </button>

      <p className={styles.stepHint} role="status" data-testid="cooking-step">
        {t(step.hintKey)}
      </p>

      {say && (
        <p className={styles.say} role="status">
          {t(say)}
        </p>
      )}

      {!asking && (step.holdKind || step.canServe) && (
        <div className={styles.bottom}>
          {step.holdKind ? (
            <button
              type="button"
              className={styles.holdButton}
              aria-pressed={held}
              data-testid="cooking-hold"
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                game.miniGames.hold(true);
              }}
              onPointerUp={() => {
                game.miniGames.hold(false);
              }}
              onPointerCancel={() => {
                game.miniGames.hold(false);
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
                  e.preventDefault();
                  game.miniGames.hold(true);
                }
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  game.miniGames.hold(false);
                }
              }}
            >
              {holdLabel}
            </button>
          ) : (
            <button
              type="button"
              className={styles.primary}
              data-testid="cooking-serve"
              onClick={() => {
                game.miniGames.serve();
              }}
            >
              {t('ui.cooking.finish')}
            </button>
          )}
        </div>
      )}
    </>
  );
}
